// src/pages/admin/mixedCompanyAssignJobs/SearchableSelect.jsx — dropdown-with-search select
// used across MixedCompanyAssignJobs for company/driver/truck pickers.
import { useEffect, useMemo, useRef, useState } from "react";
import { inputClass } from "./styles";

export default function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Select option",
  searchPlaceholder = "Search...",
  disabled = false,
  className = "",
  emptyText = "No results found.",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => options.find((opt) => String(opt.value) === String(value)) || null,
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => {
      const label = String(opt.label || "").toLowerCase();
      const keywords = Array.isArray(opt.keywords)
        ? opt.keywords.join(" ").toLowerCase()
        : String(opt.keywords || "").toLowerCase();
      return label.includes(q) || keywords.includes(q);
    });
  }, [options, search]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      return undefined;
    }
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={`${inputClass} flex items-center justify-between text-left ${
          disabled ? "cursor-not-allowed bg-gray-50 text-gray-400" : "cursor-pointer"
        }`}
      >
        <span className={selected ? "text-gray-900" : "text-gray-500"}>
          {selected?.label || placeholder}
        </span>
        <span className="ml-3 text-xs text-gray-400">▾</span>
      </button>

      {open ? (
        <div className="absolute left-0 right-0 z-30 mt-2 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
          <div className="border-b border-gray-100 p-3">
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className={inputClass}
            />
          </div>

          <div className="max-h-64 overflow-y-auto p-2">
            {filteredOptions.length ? (
              filteredOptions.map((opt) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={`flex w-full items-start justify-between rounded-xl px-3 py-2.5 text-left text-sm transition ${
                      isSelected
                        ? "bg-emerald-50 text-emerald-700"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="pr-3">{opt.label}</span>
                    {isSelected ? <span className="text-xs font-semibold">Selected</span> : null}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-4 text-sm text-gray-400">{emptyText}</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
